import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GlobalStyle,
  Page,
  Container,
  Hero,
  Eyebrow,
  HeroTitle,
  HeroMeta,
  HeroLede,
  AcceptBanner,
  Layout,
  TocWrap,
  TocTitle,
  TocList,
  TocLink,
  Content,
  Section,
  SectionTitle,
  SubTitle,
  P,
  List,
  ListItem,
  Callout,
  TableWrap,
  Table,
  ContactList,
  ContactRow,
  BackHome,
} from "./infoPageStyles";

const TOC = [
  { id: "in-app", label: "1. Delete in the App" },
  { id: "no-app", label: "2. If You Can't Access the App" },
  { id: "deleted", label: "3. What Gets Deleted" },
  { id: "retained", label: "4. What We Keep, and Why" },
  { id: "timing", label: "5. How Long It Takes" },
  { id: "subscription", label: "6. Subscriptions First" },
  { id: "other-rights", label: "7. Other Data Rights" },
  { id: "contact", label: "8. Contact Us" },
];

export default function DeleteAccountPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Your Data</Eyebrow>
          <HeroTitle>Delete Your Account</HeroTitle>
          <HeroMeta>
            <span>Effective Date: August 11, 2026</span>
          </HeroMeta>
          <HeroLede>
            You can delete your NudR account at any time — from inside the app,
            or by contacting us if you've already uninstalled it. This page
            explains both routes, what gets deleted, what we're required to
            keep, and how long it takes.
          </HeroLede>
          <AcceptBanner>
            Deleting your account is permanent and cannot be undone. If you have
            an active subscription, cancel it before you delete — deleting your
            account does not automatically cancel billing or refund amounts
            already paid.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            <Section id="in-app">
              <SectionTitle>
                <span>01</span>Delete in the App
              </SectionTitle>
              <P>
                This is the fastest way, and it's available whenever you're
                signed in:
              </P>
              <List>
                <ListItem>Open NudR and go to your profile.</ListItem>
                <ListItem>
                  Tap <strong>Settings</strong>.
                </ListItem>
                <ListItem>
                  Scroll to <strong>Account</strong> and tap{" "}
                  <strong>Delete Account</strong>.
                </ListItem>
                <ListItem>
                  Confirm when prompted. Deletion begins right away.
                </ListItem>
              </List>
              <P>
                You'll be signed out on all devices once the request is
                submitted.
              </P>
            </Section>

            <Section id="no-app">
              <SectionTitle>
                <span>02</span>If You Can't Access the App
              </SectionTitle>
              <P>
                If you've uninstalled NudR, lost access to your account, or
                can't sign in for any reason, you can still request deletion.
                Email us and we'll handle it:
              </P>
              <ContactList>
                <ContactRow>
                  <b>Deletion requests:</b>{" "}
                  <a href="mailto:privacy@nudr.app?subject=Account%20Deletion%20Request">
                    privacy@nudr.app
                  </a>
                </ContactRow>
              </ContactList>
              <P>Please include:</P>
              <List>
                <ListItem>
                  The <strong>email address on your NudR account</strong>{" "}
                  (email us from it if you can — it speeds up verification).
                </ListItem>
                <ListItem>Your username, if you remember it.</ListItem>
                <ListItem>
                  Whether you want your account fully deleted, or a copy of your
                  data instead.
                </ListItem>
              </List>
              <Callout>
                <P>
                  <strong>We'll verify it's really you</strong> before deleting
                  anything — usually by emailing the address on the account.
                  This protects you from someone else deleting your account. If
                  you don't hear back within 7 days, email us again.
                </P>
              </Callout>
            </Section>

            <Section id="deleted">
              <SectionTitle>
                <span>03</span>What Gets Deleted
              </SectionTitle>
              <P>
                When your deletion is processed, we delete or de-identify:
              </P>
              <List>
                <ListItem>
                  Your profile — display name, bio, photographs, and profile
                  attributes.
                </ListItem>
                <ListItem>Your account credentials and active sessions.</ListItem>
                <ListItem>Your matches, likes, and consent records.</ListItem>
                <ListItem>Your identity-verification document.</ListItem>
                <ListItem>Your notification and privacy settings.</ListItem>
              </List>
              <P>
                Your messages and shared media are already deleted
                automatically on a short schedule — within 24 hours of being
                read, or 30 days if never read — independently of account
                deletion.
              </P>
            </Section>

            <Section id="retained">
              <SectionTitle>
                <span>04</span>What We Keep, and Why
              </SectionTitle>
              <P>
                A small amount of information is kept after deletion, where the
                law requires it or where it's needed to protect other members:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>What</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Payment &amp; transaction records</td>
                      <td>Accounting, tax, and financial compliance.</td>
                    </tr>
                    <tr>
                      <td>Safety &amp; enforcement records</td>
                      <td>
                        Records of suspensions, bans, and confirmed reports, so
                        terminated accounts can't simply sign up again.
                      </td>
                    </tr>
                    <tr>
                      <td>Content tied to an open report</td>
                      <td>Retained while a safety investigation is active.</td>
                    </tr>
                    <tr>
                      <td>Records under legal hold</td>
                      <td>
                        Where we're subject to a preservation request or other
                        legal obligation.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <P>
                Full detail is in our <Link to="/privacy">Privacy Policy</Link>.
              </P>
            </Section>

            <Section id="timing">
              <SectionTitle>
                <span>05</span>How Long It Takes
              </SectionTitle>
              <List>
                <ListItem>
                  <strong>Your profile disappears immediately</strong> — you
                  stop appearing in discovery as soon as deletion starts.
                </ListItem>
                <ListItem>
                  <strong>Verification</strong> — for email requests, we confirm
                  it's you first.
                </ListItem>
                <ListItem>
                  <strong>Full deletion within 30 days</strong> of the request
                  (or of verification, for email requests).
                </ListItem>
                <ListItem>
                  <strong>Backups</strong> — residual copies are purged in the
                  ordinary backup cycle.
                </ListItem>
              </List>
            </Section>

            <Section id="subscription">
              <SectionTitle>
                <span>06</span>Cancel Your Subscription First
              </SectionTitle>
              <P>
                <strong>
                  Deleting your account does not automatically cancel your
                  subscription or refund what you've already paid.
                </strong>{" "}
                Cancel first, then delete.
              </P>
              <P>
                How to cancel, and when refunds are available, is set out on our{" "}
                <Link to="/refunds">Refunds &amp; Cancellation</Link> page.
              </P>
            </Section>

            <Section id="other-rights">
              <SectionTitle>
                <span>07</span>Other Data Rights
              </SectionTitle>
              <P>
                Depending on where you live, you may have the right to access,
                correct, or receive a copy of your personal information instead
                of deleting it — and to appeal if we decline a request.
              </P>
              <P>
                Email <a href="mailto:privacy@nudr.app">privacy@nudr.app</a> and
                tell us what you'd like to do. We'll verify your identity and
                respond within the timeframe required by applicable law. We
                won't treat you differently for exercising these rights.
              </P>
            </Section>

            <Section id="contact">
              <SectionTitle>
                <span>08</span>Contact Us
              </SectionTitle>
              <ContactList>
                <ContactRow>
                  <b>Privacy &amp; data requests:</b>{" "}
                  <a href="mailto:privacy@nudr.app">privacy@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>General support:</b>{" "}
                  <a href="mailto:support@nudr.app">support@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> NUDR INC, 1105 N Buena Vista
                  Street, Burbank, CA 91505, U.S.A.
                </ContactRow>
              </ContactList>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
